import { AGENT_WS_PING_MS, type BrowserWsIn, BrowserWsOut } from '@tabel/shared/schemas/agent-client';
import { useEffect, useRef, useState } from 'react';
import { getObjectId, getRole, useRole } from './role';

/*
 * WebSocket браузера 006 `/api/agent/ws` (specs/006-agent-client/contracts/http-api.md, «WebSocket браузера»; FR-020,
 * US5-1). Канал — только уведомления: номера и состояния, без текста переписки. Ленту и списки страница берёт адресами
 * (`lib/agent-api.ts`).
 * - Роль и объект — в адресе (`role`, `object`): у WebSocket нет своих заголовков, сервер переносит их в `X-Role` и
 *   `X-Object`, как у остальных запросов (`lib/role.tsx`). Сессия — cookie, `Origin` браузер ставит сам.
 * - Клиент → сервер: `{ op: 'subscribe' | 'unsubscribe', threadId }`, схема `BrowserWsIn`. Подписки помнятся и уходят
 *   заново после каждого нового соединения.
 * - Сервер → клиент: `connector`, `agents`, `install`, `changed`, `thread`, `subscribed`, `error` — схема `BrowserWsOut`;
 *   не по схеме — пропускается. Пульс `ping` слушателям не идёт.
 * - Пульс: сервер шлёт `ping` раз в 25 с. Нет ни одного сообщения дольше двух периодов (и 5 с запаса) — соединение
 *   считается оборванным: браузер его закрывает и открывает новое.
 * - Обрыв: новое соединение с паузой 1, 2, 4, 8, 10, 10… с (±20 %); после удачного — снова с 1 с. Вернулась сеть
 *   (событие `online`) — сразу. Слушатели получают `{ op: 'closed' }` и `{ op: 'open', reconnected }`: после
 *   переподключения страница догружает ленту с последнего номера и списки (US5-1).
 * - Одно соединение на вкладку и пару «роль — объект» (`useAgentSocket`): последний слушатель ушёл — соединение закрыто.
 */

/** Сообщение сервера для слушателей: всё, кроме пульса. */
export type AgentWsMessage = Exclude<BrowserWsOut, { op: 'ping' }>;
/** Событие канала: сообщение сервера, соединение открыто (`reconnected` — не первое) или оборвано. */
export type AgentWsEvent = AgentWsMessage | { op: 'open'; reconnected: boolean } | { op: 'closed' };
export type AgentWsListener = (e: AgentWsEvent) => void;

type SocketLike = Pick<WebSocket, 'readyState' | 'send' | 'close'> & {
  onopen: ((e: Event) => void) | null;
  onmessage: ((e: MessageEvent) => void) | null;
  onclose: ((e: CloseEvent) => void) | null;
  onerror: ((e: Event) => void) | null;
};
type SocketCtor = new (url: string) => SocketLike;

export interface AgentSocketOptions {
  /** Адрес соединения; функция — адрес на момент каждого нового соединения. */
  url: string | (() => string);
  /** Конструктор WebSocket; по умолчанию — `globalThis.WebSocket` на момент соединения. */
  WebSocketImpl?: typeof WebSocket;
  /** Период пульса сервера. */
  pingMs?: number;
  minDelayMs?: number;
  maxDelayMs?: number;
  /** Разброс паузы: 0 → −20 %, 1 → +20 %. */
  random?: () => number;
}

const OPEN = 1;

export class AgentSocket {
  private ws: SocketLike | null = null;
  private readonly listeners = new Set<AgentWsListener>();
  /** Подписки на разговоры: число подписчиков. */
  private readonly threads = new Map<string, number>();
  private running = false;
  private attempt = 0;
  private opened = false;
  private retry: ReturnType<typeof setTimeout> | null = null;
  private watchdog: ReturnType<typeof setTimeout> | null = null;
  private readonly onOnline = () => {
    if (!this.running || this.connected) return;
    this.attempt = 0;
    this.connect();
  };

  constructor(private readonly opts: AgentSocketOptions) {}

  get connected(): boolean {
    return this.ws?.readyState === OPEN;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    window.addEventListener('online', this.onOnline);
    this.connect();
  }

  stop(): void {
    this.running = false;
    window.removeEventListener('online', this.onOnline);
    this.clearTimers();
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      this.detach(ws);
      try {
        ws.close(1000);
      } catch {
        // уже закрыт
      }
    }
  }

  on(fn: AgentWsListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  subscribe(threadId: string): void {
    const n = this.threads.get(threadId) ?? 0;
    this.threads.set(threadId, n + 1);
    if (n === 0) this.send({ op: 'subscribe', threadId });
  }

  unsubscribe(threadId: string): void {
    const n = this.threads.get(threadId) ?? 0;
    if (n <= 1) {
      if (n === 1) {
        this.threads.delete(threadId);
        this.send({ op: 'unsubscribe', threadId });
      }
      return;
    }
    this.threads.set(threadId, n - 1);
  }

  private connect(): void {
    this.clearTimers();
    const Ctor = (this.opts.WebSocketImpl ?? globalThis.WebSocket) as unknown as SocketCtor | undefined;
    if (!Ctor) return;
    const url = typeof this.opts.url === 'function' ? this.opts.url() : this.opts.url;
    let ws: SocketLike;
    try {
      ws = new Ctor(url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws) return;
      const reconnected = this.opened;
      this.opened = true;
      this.attempt = 0;
      this.armWatchdog();
      for (const threadId of this.threads.keys()) this.send({ op: 'subscribe', threadId });
      this.emit({ op: 'open', reconnected });
    };
    ws.onmessage = (e: MessageEvent) => {
      if (this.ws !== ws) return;
      this.armWatchdog();
      const msg = parse(e.data);
      if (msg && msg.op !== 'ping') this.emit(msg);
    };
    ws.onerror = () => {
      // Причину браузер не сообщает; следом придёт `close`.
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.lost();
    };
  }

  /** Соединение оборвано: слушателям — `closed`, новое соединение — после паузы. */
  private lost(): void {
    const ws = this.ws;
    this.ws = null;
    if (ws) this.detach(ws);
    this.clearTimers();
    this.emit({ op: 'closed' });
    if (this.running) this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    const min = this.opts.minDelayMs ?? 1000;
    const max = this.opts.maxDelayMs ?? 10_000;
    const base = Math.min(max, min * 2 ** this.attempt);
    const jitter = 0.8 + 0.4 * (this.opts.random ?? Math.random)();
    const delay = Math.round(Math.min(max, Math.max(min, base * jitter)));
    this.attempt++;
    this.retry = setTimeout(() => {
      this.retry = null;
      if (this.running) this.connect();
    }, delay);
  }

  private armWatchdog(): void {
    if (this.watchdog) clearTimeout(this.watchdog);
    const ping = this.opts.pingMs ?? AGENT_WS_PING_MS;
    this.watchdog = setTimeout(
      () => {
        this.watchdog = null;
        const ws = this.ws;
        if (!ws) return;
        // Пульса нет: соединение молча умерло. Закрываем сами и не ждём `close` от браузера.
        this.detach(ws);
        try {
          ws.close(4000);
        } catch {
          // уже закрыт
        }
        this.lost();
      },
      ping * 2 + 5000,
    );
  }

  private clearTimers(): void {
    if (this.retry) clearTimeout(this.retry);
    if (this.watchdog) clearTimeout(this.watchdog);
    this.retry = null;
    this.watchdog = null;
  }

  private detach(ws: SocketLike): void {
    ws.onopen = null;
    ws.onmessage = null;
    ws.onclose = null;
    ws.onerror = null;
  }

  private send(msg: BrowserWsIn): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== OPEN) return; // уйдёт при открытии
    try {
      ws.send(JSON.stringify(msg));
    } catch {
      // соединение закрывается — подписка уйдёт после нового
    }
  }

  private emit(e: AgentWsEvent): void {
    for (const fn of [...this.listeners]) {
      try {
        fn(e);
      } catch {
        // ошибка одного слушателя не мешает другим
      }
    }
  }
}

function parse(data: unknown): BrowserWsOut | null {
  if (typeof data !== 'string') return null;
  let raw: unknown;
  try {
    raw = JSON.parse(data);
  } catch {
    return null;
  }
  const p = BrowserWsOut.safeParse(raw);
  return p.success ? p.data : null;
}

/** Адрес соединения: схема `ws`/`wss` по адресу страницы, роль и объект — как заголовки `X-Role` и `X-Object`. */
export function agentWsUrl(): string {
  const q = new URLSearchParams({ role: getRole() });
  const object = getObjectId();
  if (object) q.set('object', object);
  const scheme = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${location.host}/api/agent/ws?${q.toString()}`;
}

/* ---------- Одно соединение на вкладку ---------- */

const shared = new Map<string, { socket: AgentSocket; refs: number }>();

function acquire(key: string): AgentSocket {
  let entry = shared.get(key);
  if (!entry) {
    entry = { socket: new AgentSocket({ url: agentWsUrl }), refs: 0 };
    shared.set(key, entry);
    entry.socket.start();
  }
  entry.refs++;
  return entry.socket;
}

function release(key: string): void {
  const entry = shared.get(key);
  if (!entry) return;
  entry.refs--;
  if (entry.refs > 0) return;
  shared.delete(key);
  entry.socket.stop();
}

/**
 * Соединение вкладки для текущей роли и объекта: `onEvent` получает события канала. Смена роли или объекта — новое
 * соединение. `enabled = false` — соединения нет (функция 006 выключена, нет права). Возвращает соединение — для
 * подписки на разговор (`subscribe`, `unsubscribe`).
 */
export function useAgentSocket(onEvent: AgentWsListener, enabled = true): AgentSocket | null {
  const { role, objectId } = useRole();
  const key = `${role}|${objectId ?? ''}`;
  const handler = useRef(onEvent);
  handler.current = onEvent;
  const [socket, setSocket] = useState<AgentSocket | null>(null);
  useEffect(() => {
    if (!enabled) return;
    const s = acquire(key);
    setSocket(s);
    const off = s.on((e) => handler.current(e));
    return () => {
      off();
      release(key);
      setSocket(null);
    };
  }, [key, enabled]);
  return enabled ? socket : null;
}
